import { Box } from "@mui/material";
import React, { useContext } from "react";
import { useMessageSource } from "react-message-source";
import { BootstrapperContext } from "../../../context/bootstrapperContext";
import { LanguageContext } from "../../../context/languageContext";
import SectionFullWidthContainer from "../../../ui/layout/page/forms-container/section-full-width-container";
import SectionItemContainer from "../../../ui/layout/page/forms-container/section-item-container";
import CustomTypography from "../../../ui/widgets/typography";
import { useFormatNumber } from "../../shared/hooks/useFormatNumber";
import { defaultAddress } from "../../shared/defaults";
import { PostAddress } from "../../../api/commercial/generated-types";
import { CompanyModel } from "../types";
import { PhoneType } from "../../../types";

interface CompanyDisplayProps {
  company: CompanyModel;
}

function isSimplePhone(phone: PhoneType | string): phone is string {
  return typeof phone === "string";
}

function getPhoneNumber(company: CompanyModel): string {
  if (isSimplePhone(company.phone)) {
    return company.phone;
  } else {
    const phone: PhoneType = company.phone;
    return `${phone.code}-${phone.number}`;
  }
}

export default function CompanyDisplay({
  company,
}: CompanyDisplayProps): React.JSX.Element {
  const { getMessage } = useMessageSource();
  const { language } = useContext(LanguageContext);
  const { countries, nogaCodes } = useContext(BootstrapperContext);
  const { formatNumberAsAmount } = useFormatNumber();

  let address: PostAddress = defaultAddress();

  if (company && company.address) {
    address = company.address;
  }
  const countriesMap = countries
    ? countries.reduce(
        (acc, obj) => ({ ...acc, ...{ [obj.isoCode]: obj.name } }),
        {} as { [key: string]: string },
      )
    : {};

  const nogaCodesMap = nogaCodes
    ? nogaCodes.reduce(
        (acc, obj) => ({ ...acc, ...{ [obj.code]: obj.text } }),
        {} as { [key: string]: string },
      )
    : {};

  if (!company) {
    return null;
  }

  const legalFormName: string =
    // @ts-expect-error It will be a string
    company[`legalFormName${language.toUpperCase()}`];

  return (
    <SectionFullWidthContainer>
      <SectionItemContainer>
        <Box>
          <CustomTypography variant="h5">
            {getMessage("Company.name")}:
          </CustomTypography>
          <CustomTypography variant="body2">{company.name}</CustomTypography>
        </Box>
      </SectionItemContainer>
      <SectionItemContainer>
        <Box>
          <CustomTypography variant="h5">
            {getMessage("Company.address")}:
          </CustomTypography>
          <CustomTypography variant="body2">
            {address.street} {address.streetNr}
          </CustomTypography>
          <CustomTypography variant="body2">
            {address.zipCode} {address.city}
          </CustomTypography>
          <CustomTypography variant="body2">
            {countriesMap[address.country.alpha2]}
          </CustomTypography>
        </Box>
      </SectionItemContainer>
      {company.phone && (
        <SectionItemContainer>
          <Box>
            <CustomTypography variant="h5">
              {getMessage("Company.phone")}:
            </CustomTypography>
            <CustomTypography variant="body2">
              {getPhoneNumber(company)}
            </CustomTypography>
          </Box>
        </SectionItemContainer>
      )}
      {legalFormName && (
        <SectionItemContainer>
          <Box>
            <CustomTypography variant="h5">
              {getMessage("Company.legalForm")}:
            </CustomTypography>
            <CustomTypography variant="body2">{legalFormName}</CustomTypography>
          </Box>
        </SectionItemContainer>
      )}
      {company.industryNogaCode && (
        <SectionItemContainer>
          <Box>
            <CustomTypography variant="h5">
              {getMessage("Company.industrialSector")}:
            </CustomTypography>
            <CustomTypography variant="body2">
              {`${company.industryNogaCode} ${
                nogaCodesMap[company.industryNogaCode]
              }`}
            </CustomTypography>
          </Box>
        </SectionItemContainer>
      )}
      {company.foundationDate && (
        <SectionItemContainer>
          <Box>
            <CustomTypography variant="h5">
              {getMessage("Company.foundation")}:
            </CustomTypography>
            <CustomTypography variant="body2">
              {company.foundationDate}
            </CustomTypography>
          </Box>
        </SectionItemContainer>
      )}
      {company.capitalNom && (
        <SectionItemContainer>
          <Box>
            <CustomTypography variant="h5">
              {getMessage("Company.capitalNom")}:
            </CustomTypography>
            <CustomTypography variant="body2">
              {formatNumberAsAmount(company.capitalNom)}
            </CustomTypography>
          </Box>
        </SectionItemContainer>
      )}
      {company.enteredInTradeRegistryDate && (
        <SectionItemContainer>
          <Box>
            <CustomTypography variant="h5">
              {getMessage("Company.registeredSince")}:
            </CustomTypography>
            <CustomTypography variant="body2">
              {company.enteredInTradeRegistryDate}
            </CustomTypography>
          </Box>
        </SectionItemContainer>
      )}
    </SectionFullWidthContainer>
  );
}
